import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Trash2, Wifi, WifiOff, MessageSquare, QrCode, ServerCog } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatNumber } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  MessagingInstanceDto,
  MessagingLimitsDto,
  MessagingQrCodeDto,
} from '@kardume/types';
import {
  Badge,
  Button,
  ActionButton,
  Modal,
  TextInput,
  Switch,
  Table,
  TableContainer,
  PhoneInput,
} from '@kardume/ui';
import { useAsync } from '../../utils/useAsync';
import DataState from '../DataState';
import QrCodeModal from './QrCodeModal';

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const Panel = styled.div<{ $tone: 'info' | 'danger' }>`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  border: 1px solid ${(p) => p.theme.border};
  border-left: 3px solid ${(p) => (p.$tone === 'danger' ? p.theme.error : p.theme.petrol)};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  line-height: 1.55;
  color: ${(p) => p.theme.textSecondary};

  code {
    font-size: 0.82rem;
    color: ${(p) => p.theme.textPrimary};
  }

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Explainer = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  border-left: 3px solid ${(p) => p.theme.accent};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.85rem;
  line-height: 1.55;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.75rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h4`
  margin: 1.25rem 0 0;
  font-size: 0.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(p) => p.theme.textMuted};
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.85rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Usage = styled.span<{ $warn: boolean }>`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(p) => (p.$warn ? p.theme.warning : p.theme.textPrimary)};
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const DEFAULT_LIMITS: MessagingLimitsDto = {
  dailyLimit: 60,
  minIntervalSeconds: 45,
  maxIntervalSeconds: 120,
  windowStart: '09:00:00',
  windowEnd: '18:00:00',
  sendOnWeekends: false,
  typingIndicator: true,
  active: true,
};

interface InstanceForm {
  name: string;
  instanceName: string;
  limits: MessagingLimitsDto;
}

const EMPTY_FORM: InstanceForm = { name: '', instanceName: '', limits: DEFAULT_LIMITS };

/** `HH:mm:ss` do backend ↔ `HH:mm` do `<input type="time">`. */
const toInputTime = (value: string): string => value.slice(0, 5);
const toApiTime = (value: string): string => (value.length === 5 ? `${value}:00` : value);

/**
 * Campo numérico vazio vale 0 - o sentinela de "não informado" - e o submit barra
 * antes de enviar.
 *
 * Cair num valor padrão aqui seria pior do que parecer: apagar o "20" do teto de um
 * número em aquecimento fazia o campo exibir "60", e a próxima tecla digitada
 * concatenava no fim ("6030"). O operador triplicava o teto que este recurso existe
 * para proteger sem nunca ter digitado esse número.
 */
const toInt = (value: string): number => {
  const digits = value.replace(/\D/g, '');
  return digits === '' ? 0 : Number(digits);
};

/** 0 aparece como campo vazio, não como zero. */
const numberValue = (value: number): string => (value === 0 ? '' : String(value));

/**
 * O nome no provedor é usado em URL: espaço e acento quebrariam a rota. Normalizar
 * na digitação evita cadastrar uma instância que nunca será alcançada.
 */
const slugifyInstanceName = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9-_]/g, '-')
    .replace(/-{2,}/g, '-');

interface InstancesTabProps {
  canManage: boolean;
  canSend: boolean;
}

/**
 * Cadastro dos números usados nos disparos e, principalmente, dos limites deles.
 *
 * Os limites são atributos do número, não da campanha, porque a reputação que se
 * perde num banimento é a do número: duas campanhas no mesmo número dividem um só
 * orçamento diário.
 *
 * URL e chave da Evolution não aparecem aqui: são do servidor, vêm de variável de
 * ambiente e são exibidas apenas como diagnóstico no topo.
 */
const InstancesTab: React.FC<InstancesTabProps> = ({ canManage, canSend }) => {
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(() => backofficeApi.listMessagingInstances(), []);
  const { data: channel } = useAsync(() => backofficeApi.getMessagingChannel(), []);

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<MessagingInstanceDto | null>(null);
  const [form, setForm] = useState<InstanceForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<MessagingInstanceDto | null>(null);
  const [removing, setRemoving] = useState(false);
  const [checking, setChecking] = useState<string | null>(null);
  const [testing, setTesting] = useState<MessagingInstanceDto | null>(null);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const [qrTarget, setQrTarget] = useState<MessagingInstanceDto | null>(null);
  const [qrCode, setQrCode] = useState<MessagingQrCodeDto | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);

  const instances = data ?? [];

  const updateForm = (patch: Partial<InstanceForm>): void => setForm((prev) => ({ ...prev, ...patch }));
  const updateLimits = <K extends keyof MessagingLimitsDto>(
    key: K,
    value: MessagingLimitsDto[K]
  ): void => setForm((prev) => ({ ...prev, limits: { ...prev.limits, [key]: value } }));

  const openCreate = (): void => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setIsOpen(true);
  };

  const openEdit = (instance: MessagingInstanceDto): void => {
    setEditing(instance);
    setForm({
      name: instance.name,
      instanceName: instance.instanceName,
      limits: {
        dailyLimit: instance.dailyLimit,
        minIntervalSeconds: instance.minIntervalSeconds,
        maxIntervalSeconds: instance.maxIntervalSeconds,
        windowStart: instance.windowStart,
        windowEnd: instance.windowEnd,
        sendOnWeekends: instance.sendOnWeekends,
        typingIndicator: instance.typingIndicator,
        active: instance.active,
      },
    });
    setIsOpen(true);
  };

  const submit = async (): Promise<void> => {
    const { limits } = form;
    if (!limits.dailyLimit || !limits.minIntervalSeconds || !limits.maxIntervalSeconds) {
      showNotification('Informe o teto diário e os intervalos mínimo e máximo.', 'error');
      return;
    }
    if (limits.minIntervalSeconds > limits.maxIntervalSeconds) {
      showNotification('O intervalo mínimo não pode ser maior que o máximo.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const payloadLimits: MessagingLimitsDto = {
        ...limits,
        windowStart: toApiTime(limits.windowStart),
        windowEnd: toApiTime(limits.windowEnd),
      };
      if (editing) {
        await backofficeApi.updateMessagingInstance(editing.id, {
          name: form.name,
          limits: payloadLimits,
        });
        showNotification('Instância atualizada.', 'success');
        setIsOpen(false);
        reload();
      } else {
        const created = await backofficeApi.createMessagingInstance({
          name: form.name,
          instanceName: form.instanceName,
          limits: payloadLimits,
        });
        setIsOpen(false);
        reload();
        // O QR já vem na resposta do cadastro: abrir o modal aqui evita uma segunda
        // ida ao provedor e, mais importante, o operador não tem de descobrir sozinho
        // que ainda falta parear o celular.
        setQrCode(created.qrCode);
        setQrTarget(created.instance);
        showNotification(
          created.qrCode.alreadyConnected
            ? 'Instância cadastrada - o número já estava conectado.'
            : 'Instância criada na Evolution. Escaneie o QR Code para conectar.',
          'success'
        );
      }
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar a instância.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openConnect = async (instance: MessagingInstanceDto): Promise<void> => {
    setConnecting(instance.id);
    try {
      const result = await backofficeApi.connectMessagingInstance(instance.id);
      setQrCode(result);
      setQrTarget(instance);
      if (result.alreadyConnected) {
        showNotification('Este número já está conectado.', 'success');
        reload();
      }
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível gerar o QR Code.'), 'error');
    } finally {
      setConnecting(null);
    }
  };

  const checkConnection = async (instance: MessagingInstanceDto): Promise<void> => {
    setChecking(instance.id);
    try {
      const result = await backofficeApi.getMessagingConnection(instance.id);
      showNotification(
        result.connected
          ? `Conectado${result.phoneNumber ? ` como ${result.phoneNumber}` : ''}.`
          : `Não conectado (estado: ${result.state}). Use "Conectar" para gerar um QR Code.`,
        result.connected ? 'success' : 'error'
      );
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível consultar a instância.'), 'error');
    } finally {
      setChecking(null);
    }
  };

  const sendTest = async (): Promise<void> => {
    if (!testing) return;
    setSendingTest(true);
    try {
      await backofficeApi.sendMessagingTest(testing.id, { phoneNumber: testPhone });
      showNotification('Mensagem de teste enviada.', 'success');
      setTesting(null);
      setTestPhone('');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível enviar a mensagem de teste.'), 'error');
    } finally {
      setSendingTest(false);
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteMessagingInstance(deleting.id);
      showNotification('Instância removida do backoffice.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível remover a instância.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      {channel && (
        <Panel $tone={channel.configured ? 'info' : 'danger'}>
          <ServerCog size={18} />
          <span>
            {channel.warning ? (
              channel.warning
            ) : (
              <>
                Os disparos usam o servidor <code>{channel.baseUrl}</code>, com a chave configurada
                por variável de ambiente. Servidor e chave não são editáveis aqui: são do{' '}
                <strong>servidor</strong>, não do número.
              </>
            )}
          </span>
        </Panel>
      )}

      <Explainer>
        Os limites abaixo são o que protege o número. Recomendação para um número novo:{' '}
        <strong>20/dia na primeira semana, 40 na segunda, 60 na terceira</strong>, e só passe de 100
        depois de um mês recebendo respostas de verdade. Número que nunca recebeu resposta não deve
        subir de faixa.
      </Explainer>

      {canManage && (
        <Toolbar>
          <Button variant="primary" onClick={openCreate} disabled={!channel?.configured}>
            <Plus size={16} /> Nova instância
          </Button>
        </Toolbar>
      )}

      <DataState
        loading={loading && !data}
        error={error}
        isEmpty={instances.length === 0}
        emptyTitle="Nenhuma instância configurada"
        emptyDescription="Cadastre um número: ele é criado na Evolution e você conecta lendo o QR Code no celular."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Instância</th>
                <th>Número</th>
                <th style={{ whiteSpace: 'nowrap' }}>Hoje / teto</th>
                <th style={{ whiteSpace: 'nowrap' }}>Ritmo</th>
                <th style={{ whiteSpace: 'nowrap' }}>Janela</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {instances.map((instance) => (
                <tr key={instance.id}>
                  <td>
                    <strong>{instance.name}</strong>
                    <FieldHint>{instance.instanceName}</FieldHint>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{instance.phoneNumber || '-'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Usage $warn={instance.sentToday >= instance.dailyLimit}>
                      {formatNumber(instance.sentToday)} / {formatNumber(instance.dailyLimit)}
                    </Usage>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {instance.minIntervalSeconds}–{instance.maxIntervalSeconds}s
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {toInputTime(instance.windowStart)}–{toInputTime(instance.windowEnd)}
                    {instance.sendOnWeekends ? ' · fds' : ''}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={instance.active ? 'success' : 'secondary'}>
                      {instance.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td>
                    <RowActions>
                      {canManage && (
                        <ActionButton
                          title="Conectar (QR Code)"
                          aria-label="Conectar instância"
                          disabled={connecting === instance.id}
                          onClick={() => openConnect(instance)}
                        >
                          <QrCode size={16} />
                        </ActionButton>
                      )}
                      <ActionButton
                        title="Testar conexão"
                        aria-label="Testar conexão"
                        disabled={checking === instance.id}
                        onClick={() => checkConnection(instance)}
                      >
                        {checking === instance.id ? <WifiOff size={16} /> : <Wifi size={16} />}
                      </ActionButton>
                      {canSend && (
                        <ActionButton
                          title="Enviar mensagem de teste"
                          aria-label="Enviar mensagem de teste"
                          onClick={() => {
                            setTesting(instance);
                            setTestPhone('');
                          }}
                        >
                          <MessageSquare size={16} />
                        </ActionButton>
                      )}
                      {canManage && (
                        <>
                          <ActionButton
                            title="Editar"
                            aria-label="Editar instância"
                            onClick={() => openEdit(instance)}
                          >
                            <Edit3 size={16} />
                          </ActionButton>
                          <ActionButton
                            title="Remover"
                            aria-label="Remover instância"
                            color="#DC2626"
                            onClick={() => setDeleting(instance)}
                          >
                            <Trash2 size={16} />
                          </ActionButton>
                        </>
                      )}
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editing ? 'Editar instância' : 'Nova instância'}
        width="620px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              isLoading={submitting}
              disabled={!form.name || !form.instanceName}
            >
              {editing ? 'Salvar' : 'Criar e conectar'}
            </Button>
          </>
        }
      >
        <FormGrid style={{ marginTop: 0 }}>
          <TextInput
            label="Nome interno"
            value={form.name}
            onChange={(e) => updateForm({ name: e.target.value })}
            placeholder="Ex.: Comercial 1"
          />
          <TextInput
            label="Nome da instância"
            value={form.instanceName}
            onChange={(e) => updateForm({ instanceName: slugifyInstanceName(e.target.value) })}
            placeholder="Ex.: kardume-comercial-1"
            disabled={editing !== null}
          />
        </FormGrid>
        <FieldHint>
          {editing
            ? 'O nome da instância não pode ser alterado: é a chave que alcança este número na Evolution.'
            : 'O nome da instância identifica o número na Evolution e não poderá ser alterado depois. Se já existir uma instância com esse nome no servidor, ela é aproveitada.'}
        </FieldHint>

        <SectionTitle>Limites anti-bloqueio</SectionTitle>
        <FormGrid>
          <TextInput
            label="Teto diário (mensagens)"
            inputMode="numeric"
            value={numberValue(form.limits.dailyLimit)}
            onChange={(e) => updateLimits('dailyLimit', toInt(e.target.value))}
          />
          <div />
          <TextInput
            label="Intervalo mínimo (segundos)"
            inputMode="numeric"
            value={numberValue(form.limits.minIntervalSeconds)}
            onChange={(e) => updateLimits('minIntervalSeconds', toInt(e.target.value))}
          />
          <TextInput
            label="Intervalo máximo (segundos)"
            inputMode="numeric"
            value={numberValue(form.limits.maxIntervalSeconds)}
            onChange={(e) => updateLimits('maxIntervalSeconds', toInt(e.target.value))}
          />
        </FormGrid>
        <FieldHint>
          O intervalo entre duas mensagens é sorteado nessa faixa. Valores iguais deixam o envio com
          cadência de robô, que é justamente o que se quer evitar.
        </FieldHint>

        <FormGrid>
          <TextInput
            label="Enviar a partir de"
            type="time"
            value={toInputTime(form.limits.windowStart)}
            onChange={(e) => updateLimits('windowStart', e.target.value)}
          />
          <TextInput
            label="Enviar até"
            type="time"
            value={toInputTime(form.limits.windowEnd)}
            onChange={(e) => updateLimits('windowEnd', e.target.value)}
          />
        </FormGrid>
        <FieldHint>A janela não pode virar o dia - mensagem comercial de madrugada é denúncia.</FieldHint>

        <ToggleField>
          <Switch
            checked={form.limits.sendOnWeekends}
            onChange={(v) => updateLimits('sendOnWeekends', v)}
          />
          Enviar também no fim de semana
        </ToggleField>
        <ToggleField>
          <Switch
            checked={form.limits.typingIndicator}
            onChange={(v) => updateLimits('typingIndicator', v)}
          />
          Mostrar &quot;digitando…&quot; antes de cada mensagem
        </ToggleField>
        <ToggleField>
          <Switch checked={form.limits.active} onChange={(v) => updateLimits('active', v)} />
          Instância ativa (disponível para campanhas)
        </ToggleField>
      </Modal>

      <QrCodeModal
        isOpen={qrTarget !== null}
        instanceId={qrTarget?.id ?? null}
        instanceLabel={qrTarget?.name ?? ''}
        initialQrCode={qrCode}
        onClose={() => {
          setQrTarget(null);
          setQrCode(null);
        }}
        onConnected={reload}
      />

      <Modal
        isOpen={testing !== null}
        onClose={() => setTesting(null)}
        title="Enviar mensagem de teste"
        width="420px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setTesting(null)} disabled={sendingTest}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={sendTest} isLoading={sendingTest} disabled={!testPhone}>
              Enviar
            </Button>
          </>
        }
      >
        <PhoneInput
          label="WhatsApp de destino"
          value={testPhone}
          onChange={setTestPhone}
          helpText="Use o seu próprio número para confirmar a instância antes de disparar para valer."
        />
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Remover instância"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={removing}>
              Remover
            </Button>
          </>
        }
      >
        <ConfirmText>
          Remover <strong>{deleting?.name}</strong> do backoffice? A instância{' '}
          <strong>continua existindo na Evolution</strong> e o número segue pareado - só deixa de
          estar disponível para campanhas. Campanhas ainda não encerradas impedem a remoção.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default InstancesTab;
